import _slicedToArray from "/builds/vistaprint-org/digital/acquisition/merchandising-components/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import PropTypes from 'prop-types';
import React__default from 'react';
import { DEPRECATED_PROP_MESSAGES } from '../../constants/messages.js';
import { ButtonbarContext } from './buttonbar.context.js';
import { conditionalPropType } from '../../prop-types/conditional.prop-type.js';
import { nullishPropType } from '../../prop-types/nullish.prop-types.js';
import { filterProps } from '../core/core.utils.js';
import { deprecatedProp } from '../core/deprecated-prop.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { useId } from '../../hooks/use-id.js';
import { useControllableValue } from '../../hooks/use-controllable-value.js';
import { RenderComp } from '../core/render-comp.component.js';

function _define_property(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _object_spread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    var ownKeys = Object.keys(source);

    if (typeof Object.getOwnPropertySymbols === "function") {
      ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) {
        return Object.getOwnPropertyDescriptor(source, sym).enumerable;
      }));
    }

    ownKeys.forEach(function (key) {
      _define_property(target, key, source[key]);
    });
  }

  return target;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _object_spread_props(target, source) {
  source = source != null ? source : {};

  if (Object.getOwnPropertyDescriptors) {
    Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
  } else {
    ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

var isMultiSelect = function isMultiSelect(props) {
  return props.variant === 'multi-select';
};

var propTypes = {
  /**
  * @deprecated
  * Use width prop instead
  */
  widthVariant: deprecatedProp(PropTypes.oneOf(['standard', 'full-width']), DEPRECATED_PROP_MESSAGES.width),

  /**
  * The width of the buttonbar
  */
  width: PropTypes.oneOf(['standard', 'full-width']),

  /**
  * Name for the input element(ButtonbarButton)
  */
  name: PropTypes.string,

  /**
  * `single-select` means using radio buttons, which are mutually exclusive
  * `multi-select` means using checkbox options, of which many can be selected at once
  * `toolbar` means using regular buttons
  */
  variant: PropTypes.oneOf(['single-select', 'multi-select', 'toolbar']),
  // For single select

  /**
  * 'value' of currently selected button.
  *
  * Used by `single-select` variant.
  */
  selectedValue: conditionalPropType(isMultiSelect, nullishPropType, PropTypes.string),

  /**
  * By default selected button.
  *
  * Used by `single-select` variant.
  */
  defaultSelectedValue: conditionalPropType(isMultiSelect, nullishPropType, PropTypes.string),

  /**
  * Callback fired when selected option is changed, `event` is actual `<input>` event in case you need it.
  *
  * Used by `single-select` variant.
  */
  onSelectedValueChange: conditionalPropType(isMultiSelect, nullishPropType, PropTypes.func),
  // For multi select

  /**
  * List of 'values' of currently selected buttons.
  *
  * Used by `multi-select` variant.
  */
  selectedValues: conditionalPropType(isMultiSelect, PropTypes.arrayOf(PropTypes.string.isRequired), nullishPropType),

  /**
  * By default selected buttons.
  *
  * Used by `multi-select` variant.
  */
  defaultSelectedValues: conditionalPropType(isMultiSelect, PropTypes.arrayOf(PropTypes.string.isRequired), nullishPropType),

  /**
  * Callback fired when one of the selections' value changes.
  *
  * It is passed the updated list of `selectedValues` and the actual `<input>` event in case you need it.
  *
  * Used by `multi-select` variant.
  */
  onSelectedValuesChange: conditionalPropType(isMultiSelect, PropTypes.func, nullishPropType)
};
var propKeysToRemove = Object.keys(propTypes); // eslint-disable-next-line @typescript-eslint/no-explicit-any

var Buttonbar = renderWithRef('Buttonbar', propTypes, function (props, ref) {
  var children = props.children,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? 'single-select' : _props$variant,
      _props$widthVariant = props.widthVariant,
      widthVariant = _props$widthVariant === void 0 ? 'standard' : _props$widthVariant,
      _props$width = props.width,
      width = _props$width === void 0 ? widthVariant : _props$width,
      controlledSelectedValue = props.selectedValue,
      _props$defaultSelecte = props.defaultSelectedValue,
      defaultSelectedValue = _props$defaultSelecte === void 0 ? null : _props$defaultSelecte,
      controlledOnSelectedValueChange = props.onSelectedValueChange,
      controlledSelectedValues = props.selectedValues,
      _props$defaultSelecte2 = props.defaultSelectedValues,
      defaultSelectedValues = _props$defaultSelecte2 === void 0 ? [] : _props$defaultSelecte2,
      controlledOnSelectedValuesChange = props.onSelectedValuesChange,
      ariaLabel = props['aria-label'],
      ariaControls = props['aria-controls'],
      customName = props.name;
  var classNames = new Set(['swan-buttonbar']);
  if (width !== 'standard') classNames.add("swan-buttonbar-".concat(width));
  var name = useId(customName); // state for single-select

  var _useControllableValue = useControllableValue({
    value: controlledSelectedValue !== null && controlledSelectedValue !== void 0 ? controlledSelectedValue : undefined,
    onChange: controlledOnSelectedValueChange !== null && controlledOnSelectedValueChange !== void 0 ? controlledOnSelectedValueChange : undefined,
    defaultValue: defaultSelectedValue,
    defaultOnChange: function defaultOnChange(setValue) {
      for (var _len = arguments.length, onChangeArgs = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        onChangeArgs[_key - 1] = arguments[_key];
      }

      var newValue = onChangeArgs[0];
      setValue(newValue);
    }
  }),
      _useControllableValue2 = _slicedToArray(_useControllableValue, 2),
      selectedValue = _useControllableValue2[0],
      onSelectedValueChange = _useControllableValue2[1]; // state for multi-select


  var _useControllableValue3 = useControllableValue({
    value: controlledSelectedValues !== null && controlledSelectedValues !== void 0 ? controlledSelectedValues : undefined,
    onChange: controlledOnSelectedValuesChange !== null && controlledOnSelectedValuesChange !== void 0 ? controlledOnSelectedValuesChange : undefined,
    defaultValue: defaultSelectedValues || [],
    defaultOnChange: function defaultOnChange(setValue) {
      for (var _len2 = arguments.length, onChangeArgs = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        onChangeArgs[_key2 - 1] = arguments[_key2];
      }

      var newSelectedValues = onChangeArgs[0];
      setValue(newSelectedValues);
    }
  }),
      _useControllableValue4 = _slicedToArray(_useControllableValue3, 2),
      selectedValues = _useControllableValue4[0],
      onSelectedValuesChange = _useControllableValue4[1];

  var roleValue = 'toolbar';

  switch (variant) {
    case 'single-select':
      roleValue = 'radiogroup';
      break;

    case 'multi-select':
      roleValue = 'group';
      break;
  }

  var filteredProps = _object_spread_props(_object_spread({}, filterProps(props, null, null)), {
    role: roleValue,
    'aria-label': ariaLabel,
    'aria-controls': ariaControls
  });

  return /*#__PURE__*/React__default.createElement(ButtonbarContext.Provider, {
    value: {
      name: name,
      variant: variant,
      selectedValue: selectedValue,
      onSelectedValueChange: onSelectedValueChange,
      selectedValues: selectedValues,
      onSelectedValuesChange: onSelectedValuesChange
    }
  }, /*#__PURE__*/React__default.createElement(RenderComp, {
    root: "div",
    forwardedRef: ref,
    propKeysToRemove: propKeysToRemove,
    classNames: classNames,
    props: _object_spread({}, props, filteredProps)
  }, children));
});
Buttonbar.defaultProps = {
  width: 'standard'
};
export { Buttonbar };